import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, IconButton, Button,
  TextField, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert, Snackbar,
  Tooltip, Divider, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Switch, FormControlLabel, Avatar, Autocomplete, Tabs, Tab, Stack,
} from '@mui/material';
import {
  Add as AddIcon, Search as SearchIcon, Edit as EditIcon, Delete as DeleteIcon,
  Close as CloseIcon, Refresh as RefreshIcon, Category as CategoryIcon,
  MedicalServices as MedicalIcon, AttachMoney as MoneyIcon,
  AccessTime as TimeIcon, LocalOffer as TagIcon,
  Inventory as InventoryIcon,
} from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import { useClinicPageBreakpoints, clinicPageContainerSx } from '../../hooks/useClinicPageBreakpoints';

const categoryTypeLabels: Record<string, string> = {
  general: 'Geral', dental: 'Odontológico', medical: 'Médico', aesthetic: 'Estético',
};

const Procedures: React.FC = () => {
  const { isMobile, isTabletOrDown } = useClinicPageBreakpoints();
  const [procedures, setProcedures] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [treatmentPlans, setTreatmentPlans] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const [formData, setFormData] = useState({
    name: '', description: '', code: '', category_id: '', duration_minutes: 30,
    price: 0, cost: 0, tooth_region: '', requires_anesthesia: false,
    insurance_price: 0, is_covered_by_insurance: false, notes: '',
  });

  const [categoryForm, setCategoryForm] = useState({ name: '', description: '', category_type: 'general', color: '#8b5cf6' });

  // Materials per procedure
  const [procMaterials, setProcMaterials] = useState<any[]>([]);
  const [inventorySearch, setInventorySearch] = useState('');
  const [inventoryOptions, setInventoryOptions] = useState<any[]>([]);
  const [loadingInventory, setLoadingInventory] = useState(false);

  const fetchProcedures = useCallback(async () => {
    try {
      setLoading(true);
      const PAGE = 500;
      let page = 1;
      const merged: any[] = [];
      let reportedTotal = 0;

      while (page < 100) {
        const params: any = { limit: PAGE, page };
        if (search) params.search = search;
        if (categoryFilter) params.category_id = categoryFilter;

        const response = await axios.get(getApiUrl('/clinic/procedures'), { params });
        if (!response.data?.success) break;

        const chunk = response.data.data || [];
        const tot = typeof response.data.total === 'number' ? response.data.total : 0;
        if (page === 1) reportedTotal = tot;

        merged.push(...chunk);

        if (chunk.length < PAGE) break;
        if (reportedTotal > 0 && merged.length >= reportedTotal) break;
        page += 1;
      }

      setProcedures(merged);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search, categoryFilter]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(getApiUrl('/clinic/procedure-categories'));
      if (res.data.success) setCategories(res.data.data || []);
    } catch (err) { console.error(err); }
  };

  const fetchTreatmentPlans = async () => {
    try {
      const res = await axios.get(getApiUrl('/clinic/treatment-plans'));
      if (res.data.success) setTreatmentPlans(res.data.data || []);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchProcedures(); fetchCategories(); fetchTreatmentPlans(); }, [fetchProcedures]);

  const loadProcedureMaterials = async (procId: string) => {
    try {
      const res = await axios.get(getApiUrl(`/clinic/procedures/${procId}/materials`));
      setProcMaterials((res.data?.data || []).map((m: any) => ({
        inventory_item_id: m.inventory_item_id,
        item_name: m.clinic_inventory_items?.name || '',
        unit: m.clinic_inventory_items?.unit || 'unit',
        current_stock: m.clinic_inventory_items?.current_stock ?? 0,
        quantity_per_execution: m.quantity_per_execution,
        is_required: m.is_required,
        notes: m.notes || '',
      })));
    } catch { setProcMaterials([]); }
  };

  const searchInventoryItems = useCallback(async (term: string) => {
    if (term.length < 2) { setInventoryOptions([]); return; }
    setLoadingInventory(true);
    try {
      const res = await axios.get(getApiUrl('/inventory-module/items'), { params: { search: term, limit: 20, include_inactive: false } });
      setInventoryOptions(res.data?.data || []);
    } catch { setInventoryOptions([]); }
    finally { setLoadingInventory(false); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => { if (inventorySearch) searchInventoryItems(inventorySearch); }, 300);
    return () => clearTimeout(t);
  }, [inventorySearch, searchInventoryItems]);

  const handleSave = async () => {
    if (!formData.name) { setSnackbar({ open: true, message: 'Nome é obrigatório', severity: 'error' }); return; }
    try {
      setSaving(true);
      let procId = editingId;
      if (editingId) {
        await axios.put(getApiUrl(`/clinic/procedures/${editingId}`), formData);
      } else {
        const res = await axios.post(getApiUrl('/clinic/procedures'), formData);
        procId = res.data?.data?.id;
      }
      if (procId && procMaterials.length > 0) {
        await axios.put(getApiUrl(`/clinic/procedures/${procId}/materials`), {
          materials: procMaterials.map(m => ({
            inventory_item_id: m.inventory_item_id,
            quantity_per_execution: m.quantity_per_execution,
            is_required: m.is_required,
            notes: m.notes,
          })),
        });
      } else if (procId) {
        await axios.put(getApiUrl(`/clinic/procedures/${procId}/materials`), { materials: [] });
      }
      setSnackbar({ open: true, message: editingId ? 'Procedimento atualizado!' : 'Procedimento criado!', severity: 'success' });
      setDialogOpen(false); fetchProcedures();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Erro', severity: 'error' });
    } finally { setSaving(false); }
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.name) return;
    try {
      await axios.post(getApiUrl('/clinic/procedure-categories'), categoryForm);
      setSnackbar({ open: true, message: 'Categoria criada!', severity: 'success' });
      setCategoryDialogOpen(false); fetchCategories();
    } catch (err) {
      setSnackbar({ open: true, message: 'Erro ao criar categoria', severity: 'error' });
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Desativar este procedimento?')) return;
    try {
      await axios.delete(getApiUrl(`/clinic/procedures/${id}`));
      setSnackbar({ open: true, message: 'Procedimento desativado!', severity: 'success' });
      fetchProcedures();
    } catch (err) { setSnackbar({ open: true, message: 'Erro', severity: 'error' }); }
  };

  const formatCurrency = (v: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

  const statusLabels: Record<string, string> = {
    draft: 'Rascunho', sent: 'Enviado', approved: 'Aprovado', rejected: 'Rejeitado',
    in_progress: 'Em Andamento', completed: 'Concluído', cancelled: 'Cancelado',
  };

  return (
    <Box sx={clinicPageContainerSx}>
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
        mb: { xs: 2, md: 3 },
        gap: 2,
      }}>
        <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 700, color: '#1e1b4b', lineHeight: 1.2 }}>
          Procedimentos & Tratamentos
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, width: { xs: '100%', sm: 'auto' } }}>
          <Button variant="outlined" fullWidth={isMobile} startIcon={<CategoryIcon />} onClick={() => setCategoryDialogOpen(true)}
            sx={{ borderColor: '#8b5cf6', color: '#8b5cf6', textTransform: 'none', borderRadius: 2 }}>
            Nova Categoria
          </Button>
          <Button variant="contained" fullWidth={isMobile} startIcon={<AddIcon />} onClick={() => { setFormData({ name: '', description: '', code: '', category_id: '', duration_minutes: 30, price: 0, cost: 0, tooth_region: '', requires_anesthesia: false, insurance_price: 0, is_covered_by_insurance: false, notes: '' }); setEditingId(null); setProcMaterials([]); setDialogOpen(true); }}
            sx={{ bgcolor: '#14b8a6', '&:hover': { bgcolor: '#0d9488' }, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
            Novo Procedimento
          </Button>
        </Box>
      </Box>

      <Tabs
        value={tabValue}
        onChange={(_, v) => setTabValue(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mb: { xs: 2, md: 3 }, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 }, '& .MuiTabs-indicator': { bgcolor: '#14b8a6' } }}
      >
        <Tab label={`Catálogo (${procedures.length})`} />
        <Tab label={`Orçamentos (${treatmentPlans.length})`} />
      </Tabs>

      {tabValue === 0 && (
        <>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: { xs: 2, md: 3 }, flexWrap: 'wrap' }}>
            <TextField size="small" fullWidth placeholder="Buscar procedimento..." value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#9ca3af' }} /></InputAdornment> }}
              sx={{ minWidth: { sm: 280 }, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            <FormControl size="small" fullWidth={isMobile} sx={{ minWidth: { sm: 180 } }}>
              <InputLabel>Categoria</InputLabel>
              <Select value={categoryFilter} label="Categoria" onChange={(e) => setCategoryFilter(e.target.value)}>
                <MenuItem value="">Todas</MenuItem>
                {categories.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: '#8b5cf6' }} /></Box>
          ) : isTabletOrDown ? (
            <Stack spacing={1.5}>
              {procedures.length === 0 ? (
                <Paper sx={{ py: 6, textAlign: 'center', borderRadius: 2, border: '1px dashed #e5e7eb' }}>
                  <MedicalIcon sx={{ fontSize: 48, color: '#d1d5db', mb: 1 }} />
                  <Typography sx={{ color: '#9ca3af' }}>Nenhum procedimento cadastrado</Typography>
                </Paper>
              ) : procedures.map((proc) => (
                <Card key={proc.id} elevation={0} sx={{ borderRadius: 2, border: '1px solid #e5e7eb' }}>
                  <CardContent sx={{ py: 1.75, '&:last-child': { pb: 1.75 } }}>
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                      <Avatar sx={{ bgcolor: proc.clinic_procedure_categories?.color || '#8b5cf6', width: 40, height: 40 }}>
                        <MedicalIcon sx={{ fontSize: 20 }} />
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{proc.name}</Typography>
                        <Typography variant="caption" sx={{ color: '#6b7280' }}>{proc.code || '—'} · {proc.duration_minutes} min</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1, alignItems: 'center' }}>
                          <Chip label={proc.clinic_procedure_categories?.name || '-'} size="small"
                            sx={{ bgcolor: `${proc.clinic_procedure_categories?.color || '#8b5cf6'}20`, color: proc.clinic_procedure_categories?.color || '#8b5cf6', fontWeight: 600 }} />
                          {proc.requires_anesthesia && <Chip label="Anestesia" size="small" />}
                          <Typography variant="body2" sx={{ fontWeight: 800, color: '#10b981' }}>{formatCurrency(proc.price)}</Typography>
                          <Typography variant="caption" sx={{ color: '#6b7280' }}>custo {formatCurrency(proc.cost)}</Typography>
                          {proc.is_covered_by_insurance && (
                            <Chip label={`Conv. ${formatCurrency(proc.insurance_price || 0)}`} size="small" sx={{ bgcolor: '#dbeafe', color: '#2563eb', fontWeight: 600 }} />
                          )}
                        </Box>
                      </Box>
                    </Box>
                    <Divider sx={{ my: 1.25 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                      <Tooltip title="Editar"><IconButton onClick={() => { setFormData(proc); setEditingId(proc.id); loadProcedureMaterials(proc.id); setDialogOpen(true); }}><EditIcon sx={{ color: '#3b82f6' }} /></IconButton></Tooltip>
                      <Tooltip title="Excluir"><IconButton onClick={() => handleDelete(proc.id)}><DeleteIcon sx={{ color: '#ef4444' }} /></IconButton></Tooltip>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          ) : (
            <TableContainer component={Paper} sx={{ borderRadius: 3, border: '1px solid #e5e7eb' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f9fafb' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Procedimento</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Código</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Categoria</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Duração</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Preço</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Custo</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Convênio</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {procedures.map((proc) => (
                    <TableRow key={proc.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ bgcolor: proc.clinic_procedure_categories?.color || '#8b5cf6', width: 32, height: 32 }}>
                            <MedicalIcon sx={{ fontSize: 16 }} />
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{proc.name}</Typography>
                            {proc.requires_anesthesia && <Chip label="Anestesia" size="small" sx={{ fontSize: 9, height: 18 }} />}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell><Typography variant="body2" sx={{ color: '#6b7280' }}>{proc.code || '-'}</Typography></TableCell>
                      <TableCell>
                        <Chip label={proc.clinic_procedure_categories?.name || '-'} size="small"
                          sx={{ bgcolor: `${proc.clinic_procedure_categories?.color || '#8b5cf6'}20`, color: proc.clinic_procedure_categories?.color || '#8b5cf6', fontWeight: 600 }} />
                      </TableCell>
                      <TableCell><Typography variant="body2">{proc.duration_minutes} min</Typography></TableCell>
                      <TableCell><Typography variant="body2" sx={{ fontWeight: 700, color: '#10b981' }}>{formatCurrency(proc.price)}</Typography></TableCell>
                      <TableCell><Typography variant="body2" sx={{ color: '#6b7280' }}>{formatCurrency(proc.cost)}</Typography></TableCell>
                      <TableCell>
                        {proc.is_covered_by_insurance ? (
                          <Chip label={formatCurrency(proc.insurance_price || 0)} size="small" sx={{ bgcolor: '#dbeafe', color: '#2563eb', fontWeight: 600 }} />
                        ) : <Typography variant="body2" sx={{ color: '#9ca3af' }}>-</Typography>}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Editar"><IconButton size="small" onClick={() => { setFormData(proc); setEditingId(proc.id); loadProcedureMaterials(proc.id); setDialogOpen(true); }}><EditIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                        <Tooltip title="Excluir"><IconButton size="small" onClick={() => handleDelete(proc.id)}><DeleteIcon sx={{ fontSize: 18, color: '#ef4444' }} /></IconButton></Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {procedures.length === 0 && (
                    <TableRow><TableCell colSpan={8} sx={{ textAlign: 'center', py: 6 }}>
                      <MedicalIcon sx={{ fontSize: 48, color: '#d1d5db', mb: 1 }} />
                      <Typography sx={{ color: '#9ca3af' }}>Nenhum procedimento cadastrado</Typography>
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {tabValue === 1 && (
        <Grid container spacing={2}>
          {treatmentPlans.map((plan) => (
            <Grid item xs={12} md={6} key={plan.id}>
              <Card sx={{ borderRadius: 3, border: '1px solid #e5e7eb' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 700 }}>{plan.title}</Typography>
                      <Typography variant="caption" sx={{ color: '#6b7280' }}>
                        {plan.clinic_patients?.full_name} • {plan.plan_number}
                      </Typography>
                    </Box>
                    <Chip label={statusLabels[plan.status] || plan.status} size="small"
                      sx={{ fontWeight: 600, bgcolor: plan.status === 'approved' ? '#d1fae5' : plan.status === 'in_progress' ? '#dbeafe' : '#f3f4f6',
                        color: plan.status === 'approved' ? '#059669' : plan.status === 'in_progress' ? '#2563eb' : '#6b7280' }} />
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Typography variant="body2" sx={{ color: '#6b7280' }}>
                      {plan.clinic_treatment_plan_items?.length || 0} procedimentos
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 800, color: '#8b5cf6' }}>
                      {formatCurrency(plan.final_amount || 0)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
          {treatmentPlans.length === 0 && (
            <Grid item xs={12}>
              <Box sx={{ textAlign: 'center', py: 6, color: '#9ca3af' }}>
                <Typography>Nenhum orçamento encontrado</Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      )}

      {/* Procedure Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        scroll="paper"
        PaperProps={{
          sx: isMobile
            ? { m: 0, borderRadius: 0, height: '100%', maxHeight: '100%', display: 'flex', flexDirection: 'column' }
            : {},
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0, px: { xs: 2, sm: 3 } }}>
          {editingId ? 'Editar Procedimento' : 'Novo Procedimento'}
          <IconButton onClick={() => setDialogOpen(false)} aria-label="Fechar"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ flex: 1, overflow: 'auto', px: { xs: 2, sm: 3 } }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={8}><TextField fullWidth label="Nome *" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} size="small" /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Código TUSS" value={formData.code} onChange={(e) => setFormData({ ...formData, code: e.target.value })} size="small" /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Descrição" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} size="small" multiline rows={2} /></Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small"><InputLabel>Categoria</InputLabel>
                <Select value={formData.category_id} label="Categoria" onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}>
                  {categories.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small"><InputLabel>Duração</InputLabel>
                <Select value={formData.duration_minutes} label="Duração" onChange={(e) => setFormData({ ...formData, duration_minutes: Number(e.target.value) })}>
                  {[15,30,45,60,90,120,180].map(d => <MenuItem key={d} value={d}>{d} min</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Preço (R$)" type="number" value={formData.price} onChange={(e) => setFormData({ ...formData, price: Number(e.target.value) })} size="small" /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Custo (R$)" type="number" value={formData.cost} onChange={(e) => setFormData({ ...formData, cost: Number(e.target.value) })} size="small" /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Preço Convênio (R$)" type="number" value={formData.insurance_price} onChange={(e) => setFormData({ ...formData, insurance_price: Number(e.target.value) })} size="small" /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Região do Dente" value={formData.tooth_region} onChange={(e) => setFormData({ ...formData, tooth_region: e.target.value })} size="small" placeholder="Ex: Anterior, Posterior" /></Grid>
            <Grid item xs={6} sm={3}><FormControlLabel control={<Switch checked={formData.requires_anesthesia} onChange={(e) => setFormData({ ...formData, requires_anesthesia: e.target.checked })} />} label="Anestesia" /></Grid>
            <Grid item xs={6} sm={3}><FormControlLabel control={<Switch checked={formData.is_covered_by_insurance} onChange={(e) => setFormData({ ...formData, is_covered_by_insurance: e.target.checked })} />} label="Convênio" /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Observações" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} size="small" multiline rows={2} /></Grid>
          </Grid>

          {/* Materiais Necessários */}
          <Divider sx={{ my: 3 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <InventoryIcon sx={{ color: '#8b5cf6' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Materiais / Insumos Necessários</Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Vincule itens do estoque que são consumidos a cada execução deste procedimento.
          </Typography>

          <Autocomplete
            options={inventoryOptions.filter(o => !procMaterials.some(m => m.inventory_item_id === o.id))}
            getOptionLabel={(o: any) => `${o.name}${o.sku ? ` (${o.sku})` : ''} — ${o.current_stock} ${o.unit}`}
            loading={loadingInventory}
            inputValue={inventorySearch}
            onInputChange={(_, v) => setInventorySearch(v)}
            onChange={(_, item: any) => {
              if (!item) return;
              setProcMaterials(prev => [...prev, {
                inventory_item_id: item.id,
                item_name: item.name,
                unit: item.unit || 'unit',
                current_stock: item.current_stock ?? 0,
                quantity_per_execution: 1,
                is_required: true,
                notes: '',
              }]);
              setInventorySearch('');
              setInventoryOptions([]);
            }}
            value={null}
            renderInput={(params) => (
              <TextField {...params} size="small" label="Buscar item do estoque..." placeholder="Digite para pesquisar..."
                InputProps={{ ...params.InputProps, endAdornment: <>{loadingInventory ? <CircularProgress size={18} /> : null}{params.InputProps.endAdornment}</> }}
              />
            )}
            noOptionsText={inventorySearch.length < 2 ? 'Digite ao menos 2 caracteres' : 'Nenhum item encontrado'}
            sx={{ mb: 2 }}
          />

          {procMaterials.length > 0 && (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, mb: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f9fafb' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Item</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 100 }}>Qtd/Exec</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 80 }}>Obrig.</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 50 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {procMaterials.map((mat, idx) => (
                    <TableRow key={mat.inventory_item_id}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{mat.item_name}</Typography>
                        <Typography variant="caption" color="text.secondary">Estoque: {mat.current_stock} {mat.unit}</Typography>
                      </TableCell>
                      <TableCell>
                        <TextField size="small" type="number" value={mat.quantity_per_execution}
                          onChange={(e) => {
                            const updated = [...procMaterials];
                            updated[idx] = { ...updated[idx], quantity_per_execution: Number(e.target.value) || 1 };
                            setProcMaterials(updated);
                          }}
                          inputProps={{ min: 0.001, step: 0.1 }}
                          sx={{ width: 80 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Switch size="small" checked={mat.is_required}
                          onChange={(e) => {
                            const updated = [...procMaterials];
                            updated[idx] = { ...updated[idx], is_required: e.target.checked };
                            setProcMaterials(updated);
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => setProcMaterials(prev => prev.filter((_, i) => i !== idx))}>
                          <DeleteIcon sx={{ fontSize: 16, color: '#ef4444' }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 2 }, flexShrink: 0, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: 1 }}>
          <Button fullWidth={isMobile} onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button fullWidth={isMobile} variant="contained" onClick={handleSave} disabled={saving}
            sx={{ bgcolor: '#14b8a6', '&:hover': { bgcolor: '#0d9488' }, textTransform: 'none', fontWeight: 600 }}>
            {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Category Dialog */}
      <Dialog open={categoryDialogOpen} onClose={() => setCategoryDialogOpen(false)} maxWidth="xs" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ fontWeight: 700, px: { xs: 2, sm: 3 } }}>Nova Categoria</DialogTitle>
        <DialogContent dividers sx={{ px: { xs: 2, sm: 3 } }}>
          <Grid container spacing={2}>
            <Grid item xs={12}><TextField fullWidth label="Nome *" value={categoryForm.name} onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })} size="small" /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Descrição" value={categoryForm.description} onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })} size="small" /></Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small"><InputLabel>Tipo</InputLabel>
                <Select value={categoryForm.category_type} label="Tipo" onChange={(e) => setCategoryForm({ ...categoryForm, category_type: e.target.value })}>
                  {Object.entries(categoryTypeLabels).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}><TextField fullWidth label="Cor" type="color" value={categoryForm.color} onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value })} size="small" /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 2 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: 1 }}>
          <Button fullWidth={isMobile} onClick={() => setCategoryDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button fullWidth={isMobile} variant="contained" onClick={handleSaveCategory}
            sx={{ bgcolor: '#14b8a6', '&:hover': { bgcolor: '#0d9488' }, textTransform: 'none' }}>Criar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Procedures;
